import React, {createContext, useContext, useState, useRef, useCallback, useEffect, PropsWithChildren} from 'react'
import {Box, useTheme, useMediaQuery} from '@mui/material'
import InlineMessage from './InlineMessage'

type Severity = 'success' | 'error' | 'warning' | 'info'

export interface ShowToastOpts {
  severity: Severity
  message: string
  title?: string
  duration?: number
  // clicking it runs onClick and closes the toast
  action?: {label: string; onClick: () => void}
}

interface Toast {
  id: number
  severity: Severity
  message: string
  title?: string
  duration: number
  action?: {label: string; onClick: () => void}
}

interface ToastContextValue {
  showToast: (opts: ShowToastOpts) => void
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined)

export function ToastProvider({children}: PropsWithChildren<{}>): JSX.Element {
  const [toasts, setToasts] = useState<Toast[]>([])
  const timers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map())
  const idCounter = useRef(0)

  const removeToast = useCallback((id: number) => {
    const timer = timers.current.get(id)
    if (timer !== undefined) {
      clearTimeout(timer)
      timers.current.delete(id)
    }
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  const showToast = useCallback((opts: ShowToastOpts) => {
    const id = ++idCounter.current
    const duration = opts.duration ?? 3000

    setToasts(prev => [...prev, {
      id,
      severity: opts.severity,
      message: opts.message,
      title: opts.title,
      duration,
      action: opts.action,
    }])

    const timer = setTimeout(() => {
      removeToast(id)
    }, duration)

    timers.current.set(id, timer)
  }, [removeToast])

  // Clear all pending timers on unmount
  useEffect(() => {
    const currentTimers = timers.current
    return () => {
      currentTimers.forEach(timer => clearTimeout(timer))
      currentTimers.clear()
    }
  }, [])

  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))

  const containerSx = isDesktop
    ? {top: 16, right: 16}
    : {top: 16, left: '50%', transform: 'translateX(-50%)'}

  return (
    <ToastContext.Provider value={{showToast}}>
      {children}
      <Box
        aria-live="polite"
        sx={{
          position: 'fixed',
          zIndex: theme.zIndex.snackbar,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          width: '90vw',
          maxWidth: '480px',
          ...containerSx,
        }}
      >
        {toasts.map(toast => (
          <Box key={toast.id} role={toast.severity === 'error' ? 'alert' : undefined}>
            <InlineMessage
              severity={toast.severity}
              title={toast.title}
              onClose={() => removeToast(toast.id)}
              action={toast.action && {
                label: toast.action.label,
                onClick: () => { removeToast(toast.id); toast.action?.onClick() },
              }}
              sx={{boxShadow: '0 4px 16px rgba(0,0,0,0.12)'}}
            >
              {toast.message}
            </InlineMessage>
          </Box>
        ))}
      </Box>
    </ToastContext.Provider>
  )
}

export function useToast(): {
  showToast: (opts: ShowToastOpts) => void
  success: (message: string, opts?: Omit<ShowToastOpts, 'severity' | 'message'>) => void
  error: (message: string, opts?: Omit<ShowToastOpts, 'severity' | 'message'>) => void
  warning: (message: string, opts?: Omit<ShowToastOpts, 'severity' | 'message'>) => void
  info: (message: string, opts?: Omit<ShowToastOpts, 'severity' | 'message'>) => void
} {
  const ctx = useContext(ToastContext)
  if (ctx === undefined) {
    throw new Error('useToast must be used within a ToastProvider')
  }

  const {showToast} = ctx

  const success = useCallback(
    (message: string, opts?: Omit<ShowToastOpts, 'severity' | 'message'>) =>
      showToast({severity: 'success', message, ...opts}),
    [showToast]
  )

  const error = useCallback(
    (message: string, opts?: Omit<ShowToastOpts, 'severity' | 'message'>) =>
      showToast({severity: 'error', message, ...opts}),
    [showToast]
  )

  const warning = useCallback(
    (message: string, opts?: Omit<ShowToastOpts, 'severity' | 'message'>) =>
      showToast({severity: 'warning', message, ...opts}),
    [showToast]
  )

  const info = useCallback(
    (message: string, opts?: Omit<ShowToastOpts, 'severity' | 'message'>) =>
      showToast({severity: 'info', message, ...opts}),
    [showToast]
  )

  return {showToast, success, error, warning, info}
}
